import React from 'react';
import { Images } from 'utils';
import { SetupGuideDrawer } from './setupGuideDrawer';
import { GuideStep, GuideCallout, GuideSection, GuideImage, GuideLinkButton } from './guideParts';
import alertWarning from "../../../../assets/images/icons/alertWarning.svg";

/**
 * Office Ally setup guide (Figma node 34892-118847).
 *
 * Copy transcribed from the design. The inline screenshots are placeholder slots
 * (`GuideImage` with no `src`) — export them from Figma into
 * `assets/images/guide/` and pass the imported asset as `src` to finalize.
 */
export const OfficeAllyGuide = ({ open, onClose }) => {
    return (
        <SetupGuideDrawer open={open} onClose={onClose} title="Connect Office Ally to TherapyLake">
            <p className="guide-intro">
                Use SFTP (Secure File Transfer Protocol) to securely exchange claim and response files between Office Ally
                and TherapyLake.
            </p>

            <GuideCallout tone="info" title="Before you start">
                You&apos;ll need an active Office Ally account and SFTP credentials issued by Office Ally. Your regular
                Office Ally login credentials are not the same as your SFTP credentials.
            </GuideCallout>

            <GuideStep index="1" title="Request SFTP access" subtitle="Request your Office Ally SFTP credentials">
                <p>
                    Email Office Ally Support from the authorized email address associated with your Office Ally account
                    and request SFTP access. Include:
                </p>
                <ul className="guide-list">
                    <li>Your Office Ally Account Number and/or Username</li>
                    <li>A request to enable SFTP access</li>
                </ul>
                <GuideLinkButton href="mailto:support@officeally.com">Email Office Ally Support</GuideLinkButton>
                <p className="guide-note">Office Ally requires the request to come from an authorized contact email address.</p>
            </GuideStep>

            <GuideStep index="2" title="Receive your SFTP credentials" subtitle="Check your email for your SFTP credentials">
                <p>
                    Once Office Ally activates SFTP access, they&apos;ll send the credentials needed to establish the
                    connection. You&apos;ll need:
                </p>
                <ul className="guide-list">
                    <li>SFTP Username</li>
                    <li>SFTP Password</li>
                </ul>
                <GuideCallout tone="neutral" icon={Images.checkedVerify}>
                    Keep these credentials secure. You&apos;ll enter them in TherapyLake in the next step.
                </GuideCallout>
            </GuideStep>

            <GuideStep index="3" title="Connect Office Ally in TherapyLake" subtitle="Enter your SFTP credentials">
                <p>
                    Return to TherapyLake and go to <b>System → Integrations → Office Ally</b>, then click <b>Connect</b>.
                </p>
                <GuideImage caption="Integrations → Office Ally → Connect" src={Images.systemAllyGuide} alt={'systemAllyGuide'}/>
                <p className="guide-ol-step">1. Click Connect next to Office Ally.</p>
                <p>Enter the SFTP credentials provided by Office Ally, then click Save to connect the integration.</p>
                <ul className="guide-list">
                    <li>SFTP Username</li>
                    <li>SFTP Password</li>
                </ul>
                <GuideImage caption="Office Ally connect form" src={Images.sftpIntegration} alt='sftpIntegration'/>
                <p className="guide-ol-step">2. Enter the SFTP credentials provided by Office Ally and click Save.</p>
            </GuideStep>

            <GuideStep index="4" title="Configure file processing" subtitle="Choose how TherapyLake handles SFTP files">
                <p>
                    Enable this option if TherapyLake should automatically manage files in the configured SFTP folder
                    after processing them.
                </p>
                <GuideCallout tone="warning" icon={Images.alertWarning}>
                    Before enabling this option, make sure the folder is dedicated to TherapyLake and does not contain
                    files that need to remain unchanged.
                </GuideCallout>
            </GuideStep>

            <GuideSection title="Complete the connection">
                <p>
                    Click Save after entering your credentials and choosing your file-processing preference. Once the
                    SFTP credentials are validated successfully, Office Ally will appear as connected on the Integrations
                    page.
                </p>
                <GuideCallout tone="success" icon={Images.isReadyGreen}>
                    Office Ally is ready to use once the SFTP credentials have been validated successfully.
                </GuideCallout>
            </GuideSection>

            <GuideSection title="Having trouble connecting?">
                <ul className="guide-list">
                    <li>Make sure you&apos;re using your Office Ally SFTP credentials, not your regular Office Ally login.</li>
                    <li>Check that the username and password were entered correctly.</li>
                    <li>If your SFTP access has not been activated yet, contact Office Ally Support.</li>
                </ul>
            </GuideSection>
        </SetupGuideDrawer>
    );
};
